// =============================================================================
// selectors/forYouToday.ts — scope the coaching card, then compute it.
// -----------------------------------------------------------------------------
// Two UI-state terms resolved before `domain/` is reached, exactly as `selectKpis`
// does: which accounts are selected, and which of them are excluded from metrics.
//
// ⚠ THE ACCOUNT SELECTION IS APPLIED TWICE, and both are load-bearing:
//
//   1. `tradesForSelection` — what the user is looking at.
//   2. `countsForMetrics`   — what is allowed to move a number. A SIM or replay
//      account is dropped here, so practice trades cannot name the trader's
//      strongest edge or their costliest leak. Under an excluded-ONLY selection
//      the exclusion lifts, because the user has deliberately drilled into it.
//
// ⚠ NO FACET FILTER, and NO DATE FILTER. The old client reaches this card through
// `tradesForActiveAccounts()`, which bypasses both — its own comment says the
// date-bypass callers "stay a full picture on purpose". Applying the global
// Pattern facet here would be self-defeating in the obvious way: filter to one
// setup and it becomes your strongest edge.
// =============================================================================

import { forYouToday, type CoachingTrade, type ForYouToday } from '@domain/forYouToday';
import {
  tradesCountingForMetrics,
  type AccountFilterAccount,
  type AccountFilterTrade,
  type AccountSelection,
} from './accounts';

export interface ForYouTodayView<T extends CoachingTrade & AccountFilterTrade> {
  /**
   * ⚠ THE WHOLE BOOK. `io/sync/coaching.ts` is the only read that produces it,
   * and it takes no bounds — see the note there on why no `ReadReceipt` is
   * involved. Handing a date-scoped array in would produce a confident claim
   * about the wrong setup.
   */
  trades: readonly T[];
  accounts: readonly AccountFilterAccount[];
  /** Empty means "all accounts" — not "no accounts". */
  selection: AccountSelection;
  /** id → display name; `null` when the catalogue no longer resolves the id. */
  namePattern: (id: string) => string | null;
}

export function selectForYouToday<T extends CoachingTrade & AccountFilterTrade>(
  view: ForYouTodayView<T>,
): ForYouToday {
  return forYouToday(
    tradesCountingForMetrics(view.trades, view.selection, view.accounts),
    view.namePattern,
  );
}

// ── Types re-exported for ui/ ────────────────────────────────────────────────
//
// ⚠ `ui/` may not import `domain/` at all, and naming `ForYouToday` in a prop
// type still counts as an import.
export type {
  ForYouToday, EdgeFinding, LeakFinding, CoachingTrade,
} from '@domain/forYouToday';
export { MIN_TRADES } from '@domain/forYouToday';
